import React, { useState, useEffect } from 'react';
import {
  Gamepad2,
  Target,
  Users,
  Trophy,
  Crown,
  Play,
  RotateCcw,
} from 'lucide-react';
import { api } from '../services/api';
import './QuizGame.css';

const PLAYER_COLORS = [
  { id: 'red', name: 'Vermelho', color: '#ff4444', emoji: '🚗' },
  { id: 'green', name: 'Verde', color: '#44ff44',  emoji: '🎩' },
  { id: 'yellow', name: 'Amarelo', color: '#ffdd44', emoji: '👞' },
  { id: 'blue', name: 'Azul', color: '#4444ff', emoji: '🤖' },
];

const QuizGame = ({ pdfData }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [numQuestions, setNumQuestions] = useState(10);
  const [gameStarted, setGameStarted] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answers, setAnswers] = useState({}); 
  const [players, setPlayers] = useState([]);
  const [scores, setScores] = useState({}); 
  const [showResults, setShowResults] = useState(false);
  const [gameMode, setGameMode] = useState('solo'); 
  const [playerName, setPlayerName] = useState('');
  const [newPlayerName, setNewPlayerName] = useState('');
  const [selectedColorId, setSelectedColorId] = useState(PLAYER_COLORS[0].id);
  const [showRanking, setShowRanking] = useState(false);

  useEffect(() => {
    if (!pdfData) {
      resetGame();
    }
  }, [pdfData]);

  const resetGame = () => {
    setQuestions([]);
    setGameStarted(false);
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setAnswers({});
    setPlayers([]);
    setScores({});
    setShowResults(false);
    setShowRanking(false);
    setPlayerName('');
    setNewPlayerName('');
    setSelectedColorId(PLAYER_COLORS[0].id);
  };

  const getCurrentPlayer = () => {
    if (gameMode !== 'multiplayer' || players.length === 0) return null;
    const playerIndex = currentQuestionIndex % players.length;
    return players[playerIndex];
  };

  const currentPlayer = getCurrentPlayer();
  const currentPlayerHasAnswered = currentPlayer 
    ? answers[currentQuestionIndex]?.[currentPlayer.id] !== undefined
    : false;

  const generateQuiz = async () => {
    if (!pdfData || !pdfData.text) {
      setError('Nenhum PDF carregado para gerar quiz.');
      return;
    }

    setLoading(true);
    setError(null);
    resetGame();

    try {
      const response = await api.post('/api/generate-quiz', {
        fileId: pdfData.fileId,
        text: pdfData.text,
        numQuestions: Math.min(Math.max(parseInt(numQuestions) || 10, 1), 30),
      });

      if (response.data.error) {
        setError(response.data.error);
        setLoading(false);
        return;
      }

      if (response.data.questions && response.data.questions.length > 0) {
        setQuestions(response.data.questions);
        const shuffledQuestions = response.data.questions.map(q => {
          const correctAnswer = q.alternativas[q.resposta_correta];
          const shuffled = [...q.alternativas].sort(() => Math.random() - 0.5);
          const newCorrectIndex = shuffled.indexOf(correctAnswer);
          return {
            ...q,
            alternativas: shuffled,
            resposta_correta: newCorrectIndex,
          };
        });
        setQuestions(shuffledQuestions);
        setLoading(false);
      } else {
        setError('Não foi possível gerar perguntas.');
        setLoading(false);
      }
    } catch (err) {
      console.error('Erro ao gerar quiz:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Erro ao gerar quiz.';
      
      if (errorMessage.includes('503') || errorMessage.includes('overloaded') || errorMessage.includes('UNAVAILABLE')) {
        setError('O serviço de IA está temporariamente sobrecarregado. Por favor, tente novamente em alguns instantes.');
      } else {
        setError(errorMessage);
      }
      setLoading(false);
    }
  };

  const startGame = () => {
    if (gameMode === 'solo') {
      if (!playerName.trim()) {
        setError('Digite seu nome para começar.');
        return;
      }
      const soloPlayer = {
        id: 'solo',
        name: playerName,
        color: PLAYER_COLORS[0],
      };
      setPlayers([soloPlayer]);
      setScores({ solo: 0 });
    } else {
      if (players.length < 1) {
        setError('Adicione pelo menos um jogador.');
        return;
      }
      
      const initialScores = {};
      players.forEach(p => {
        initialScores[p.id] = 0;
      });
      setScores(initialScores);
    }
    
    setGameStarted(true);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setShowResults(false);
    setShowRanking(false);
    setError(null);
  };

  const addPlayer = (nameToUse = newPlayerName, colorId = selectedColorId) => {
    const sanitizedName = String(nameToUse || '').trim();
    if (!sanitizedName) {
      setError('Digite o nome do jogador antes de adicionar.');
      return;
    }

    const duplicated = players.some(
      (player) => player.name.toLowerCase() === sanitizedName.toLowerCase()
    );
    if (duplicated) {
      setError('Já existe um jogador com esse nome.');
      return;
    }

    const color = PLAYER_COLORS.find(c => c.id === colorId);
    if (!color) return;
    
    const playerId = `player_${Date.now()}_${Math.random()}`;
    const newPlayer = {
      id: playerId,
      name: sanitizedName,
      color: color,
    };
    
    setPlayers([...players, newPlayer]);
    setScores({ ...scores, [playerId]: 0 });
    setNewPlayerName('');
    setError(null);
  };

  const removePlayer = (playerId) => {
    setPlayers(players.filter(p => p.id !== playerId));
    const newScores = { ...scores };
    delete newScores[playerId];
    setScores(newScores);
  };

  const handleAnswerSelect = (answerIndex, playerId) => {
    if (gameMode === 'solo' && selectedAnswer !== null) return;
  
    if (gameMode === 'multiplayer') {
      const currentPlayerForQuestion = getCurrentPlayer();
      if (!currentPlayerForQuestion || currentPlayerForQuestion.id !== playerId) {
        return; 
      }
      if (answers[currentQuestionIndex]?.[playerId] !== undefined) {
        return; 
      }
    }
    
    const currentQ = questions[currentQuestionIndex];
    
    const newAnswers = { ...answers };
    if (!newAnswers[currentQuestionIndex]) {
      newAnswers[currentQuestionIndex] = {};
    }
    newAnswers[currentQuestionIndex][playerId] = answerIndex;
    setAnswers(newAnswers);
    
    if (gameMode === 'solo') {
      setSelectedAnswer(answerIndex);
    }
    
    const isCorrect = answerIndex === currentQ.resposta_correta;
    
    if (isCorrect) {
      setScores({
        ...scores,
        [playerId]: (scores[playerId] || 0) + 1,
      });
    }
    
    if (gameMode === 'solo') {
      setShowResults(true);
    }
    
    if (gameMode === 'multiplayer') {
      setTimeout(() => {
        setShowResults(true);
        
        setTimeout(() => {
          if (currentQuestionIndex < questions.length - 1) {
            nextQuestion();
          } else {
            setShowRanking(true);
          }
        }, 2000); 
      }, 500);
    }
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer(null);
      setShowResults(false);
    } else {
      setShowRanking(true);
    }
  };

  const getRanking = () => {
    return Object.entries(scores)
      .map(([playerId, score]) => {
        const player = players.find(p => p.id === playerId);
        return {
          playerId,
          name: player?.name || 'Desconhecido',
          color: player?.color || PLAYER_COLORS[0],
          score,
        };
      })
      .sort((a, b) => b.score - a.score);
  };

  const currentQuestion = questions[currentQuestionIndex];
  const ranking = getRanking();
  const currentAnswersCount = Object.keys(answers[currentQuestionIndex] || {}).length;

  if (!gameStarted && questions.length === 0) {
    return (
      <div className="quiz-container">
        <div className="quiz-header">
          <h3>
            <Gamepad2 size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Quiz Multiplayer
          </h3>
        </div>

        <div className="quiz-setup">
          <label htmlFor="num-questions"></label>
          <input
            type="number"
            id="num-questions"
            min="1"
            max="30"
            value={numQuestions}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '') {
                setNumQuestions('');
                return;
              }
              const numValue = parseInt(value);
              if (!isNaN(numValue)) {
                if (numValue > 50) {
                  setNumQuestions(50)
                } else {
                  setNumQuestions(value);
                }
              }
            }}
            onBlur={(e) => {
              const value = parseInt(e.target.value);
              if (isNaN(value) || value < 1) {
                setNumQuestions(1);
              } else if (value > 50) {
                setNumQuestions(50);
              }
            }}
            className="num-questions-input"
          />
          <button onClick={generateQuiz} disabled={loading} className="generate-button">
            {loading ? 'Gerando...' : 'Gerar Perguntas'}
          </button>
          {error && <div className="error-message">{error}</div>}
          {loading && (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Gerando perguntas ...</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!gameStarted && questions.length > 0) {
    return (
      <div className="quiz-container">
        <div className="quiz-header">
          <h3>
            <Gamepad2 size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Configurar Jogo
          </h3>
        </div>

        <div className="game-setup">
          <div className="game-mode-selector">
            <label>
              <input
                type="radio"
                name="gameMode"
                value="solo"
                checked={gameMode === 'solo'}
                onChange={(e) => {
                  setGameMode(e.target.value);
                  setError(null);
                }}
              />
              <Target size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
              Modo Solo
            </label>
            <label>
              <input
                type="radio"
                name="gameMode"
                value="multiplayer"
                checked={gameMode === 'multiplayer'}
                onChange={(e) => {
                  setGameMode(e.target.value);
                  setError(null);
                }}
              />
              <Users size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
              Modo Multiplayer
            </label>
          </div>

          {gameMode === 'solo' ? (
            <div className="solo-setup">
              <label htmlFor="player-name"></label>
              <input
                type="text"
                id="player-name"
                value={playerName}
                onChange={(e) => {
                  setPlayerName(e.target.value);
                  setError(null);
                }}
                placeholder="Digite seu nome"
                className="player-name-input"
              />
            </div>
          ) : (
            <div className="multiplayer-setup">
              <h4>Jogadores ({players.length})</h4>
              <div className="players-list">
                {players.map(player => (
                  <div key={player.id} className="player-tag" style={{ borderColor: player.color.color }}>
                    <span style={{ color: player.color.color }}>{player.color.emoji}</span>
                    <span>{player.name}</span>
                    <button onClick={() => removePlayer(player.id)} className="remove-player">×</button>
                  </div>
                ))}
              </div>
              <div className="add-players">
                <label htmlFor="new-player-name">Adicionar jogador</label>
                <div className="add-player-controls">
                  <input
                    type="text"
                    id="new-player-name"
                    value={newPlayerName}
                    onChange={(e) => {
                      setNewPlayerName(e.target.value);
                      setError(null);
                    }}
                    placeholder="Nome do jogador"
                    className="add-player-input"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addPlayer();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="add-player-button"
                    onClick={() => addPlayer()}
                    disabled={!newPlayerName.trim()}
                  >
                    Adicionar
                  </button>
                </div>
                <div className="color-buttons">
                  {PLAYER_COLORS.map(color => (
                    <button
                      type="button"
                      key={color.id}
                      onClick={() => setSelectedColorId(color.id)}
                      className={`color-button ${selectedColorId === color.id ? 'is-selected' : ''}`}
                      style={{ backgroundColor: color.color }}
                      title={`Selecionar ${color.name}`}
                    >
                      {color.emoji}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <button onClick={startGame} className="start-game-button">
            <Play size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Iniciar Jogo
          </button>
          <button onClick={resetGame} className="reset-button">
            <RotateCcw size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Voltar
          </button>
          {error && <div className="error-message">{error}</div>}
        </div>
      </div>
    );
  }

  if (showRanking) {
    return (
      <div className="quiz-container">
        <div className="quiz-header">
          <h3>
            <Trophy size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Ranking Final
          </h3>
          <p>Resultados do quiz:</p>
        </div>
        <div className="ranking-container">
          {ranking.map((player, index) => (
            <div
              key={player.playerId}
              className={`ranking-item ${index === 0 ? 'first' : ''}`}
              style={{ borderColor: player.color.color }}
            >
              <div className="ranking-position">
                {index === 0 ? (
                  <Crown size={24} style={{ color: '#FFD700' }} />
                ) : index === 1 ? (
                  <Trophy size={24} style={{ color: '#C0C0C0' }} />
                ) : index === 2 ? (
                  <Trophy size={24} style={{ color: '#CD7F32' }} />
                ) : (
                  `${index + 1}º`
                )}
              </div>
              <div className="ranking-info">
                <span className="ranking-emoji" style={{ color: player.color.color }}>
                  {player.color.emoji}
                </span>
                <span className="ranking-name">{player.name}</span>
              </div>
              <div className="ranking-score">
                {player.score} / {questions.length}
              </div>
            </div>
          ))}
        </div>
        <div className="ranking-actions">
          <button onClick={() => { resetGame(); setQuestions([]); }} className="new-game-button">
            <RotateCcw size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Novo Jogo
          </button>
          <button onClick={() => { setShowRanking(false); resetGame(); startGame(); }} className="play-again-button">
            <Play size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Jogar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="quiz-container">
      <div className="quiz-header">
        <h3>
          <Gamepad2 size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          Quiz em Andamento
        </h3>
        <div className="quiz-progress">
          Pergunta {currentQuestionIndex + 1} de {questions.length}
          <div className="progress-bar-container">
            <div
              className="progress-bar"
              style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
            ></div>
          </div>
        </div>
        <div className="quiz-score-strip">
          {ranking.map((player) => (
            <div
              key={player.playerId}
              className={`score-pill ${currentPlayer?.id === player.playerId ? 'active' : ''}`}
              style={{ '--player-color': player.color.color }}
            >
              <span className="score-pill-emoji">{player.color.emoji}</span>
              <span className="score-pill-name">{player.name}</span>
              <span className="score-pill-points">{player.score}</span>
            </div>
          ))}
          {gameMode === 'multiplayer' ? (
            <div className="answered-pill">
              Respostas nesta rodada: {currentAnswersCount} / {players.length}
            </div>
          ) : null}
        </div>
      </div>

      {currentQuestion && (
        <div className="question-container" key={`question-${currentQuestionIndex}`}>
          <h4 className="question-text">{currentQuestion.pergunta}</h4>
          
          {gameMode === 'multiplayer' && currentPlayer && !showResults && (
            <div className="current-player-indicator" style={{ borderColor: currentPlayer.color.color, backgroundColor: `${currentPlayer.color.color}15` }}>
              <div className="current-player-content">
                <span style={{ fontSize: '2rem' }}>{currentPlayer.color.emoji}</span>
                <div>
                  <h5 style={{ margin: 0, color: currentPlayer.color.color }}>
                    Vez de: {currentPlayer.name}
                  </h5>
                  <p style={{ margin: '5px 0 0 0', fontSize: '0.9rem', color: '#666' }}>
                    Escolha sua resposta abaixo
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {gameMode === 'multiplayer' && showResults && currentPlayer && (
            <div className="players-status-summary">
              {(() => {
                const playerAnswer = answers[currentQuestionIndex]?.[currentPlayer.id];
                const isCorrect = playerAnswer === currentQuestion.resposta_correta;
                return (
                  <div 
                    className={`player-status-summary ${isCorrect ? 'correct' : 'incorrect'}`}
                    style={{ borderColor: currentPlayer.color.color }}
                  >
                    <span style={{ color: currentPlayer.color.color, fontSize: '1.5rem' }}>
                      {currentPlayer.color.emoji}
                    </span>
                    <span><strong>{currentPlayer.name}</strong></span>
                    <span>{isCorrect ? '✓ Acertou! +1 ponto' : '✗ Errou'}</span>
                  </div>
                );
              })()}
            </div>
          )}
          
          <div className="answers-container">
            {currentQuestion.alternativas.map((alternativa, index) => {
              const isCorrect = index === currentQuestion.resposta_correta;
              const playerAnswers = answers[currentQuestionIndex] || {};
              const playersWhoAnswered = Object.entries(playerAnswers)
                .filter(([_, answerIndex]) => answerIndex === index)
                .map(([playerId]) => players.find(p => p.id === playerId))
                .filter(Boolean);

              const isSelectedSolo = gameMode === 'solo' && selectedAnswer === index;
              const isSelectedByCurrentPlayer =
                gameMode === 'multiplayer' &&
                currentPlayer &&
                answers[currentQuestionIndex]?.[currentPlayer.id] === index;

              return (
                <button
                  key={index}
                  onClick={() => {
                    if (gameMode === 'solo') {
                      handleAnswerSelect(index, players[0].id);
                    } else if (gameMode === 'multiplayer' && currentPlayer && !currentPlayerHasAnswered) {
                      handleAnswerSelect(index, currentPlayer.id);
                    }
                  }}
                  className={`answer-button ${
                    isSelectedSolo || isSelectedByCurrentPlayer ? 'selected' : ''
                  } ${
                    showResults && isCorrect ? 'correct' : ''
                  } ${
                    showResults && isSelectedSolo && !isCorrect ? 'incorrect' : ''
                  } ${
                    showResults && gameMode === 'multiplayer' && currentPlayer && 
                    answers[currentQuestionIndex]?.[currentPlayer.id] === index &&
                    index === currentQuestion.resposta_correta
                      ? 'player-correct' : ''
                  }`}
                  disabled={
                    (showResults && gameMode === 'solo') ||
                    (gameMode === 'multiplayer' && (!currentPlayer || currentPlayerHasAnswered || showResults))
                  }
                >
                  <span className="answer-letter">{String.fromCharCode(65 + index)}</span>
                  <span className="answer-text">{alternativa}</span>
                  {showResults && isCorrect && <span className="correct-mark">✓</span>}
                  {playersWhoAnswered.length > 0 && (
                    <div className="player-answers">
                      {playersWhoAnswered.map(player => {
                        const playerAnswer = answers[currentQuestionIndex]?.[player.id];
                        const playerGotItRight = playerAnswer === currentQuestion.resposta_correta;
                        return (
                          <span 
                            key={player.id} 
                            style={{ 
                              color: player.color.color,
                              opacity: showResults && !playerGotItRight ? 0.5 : 1
                            }}
                            title={showResults ? (playerGotItRight ? `${player.name} acertou!` : `${player.name} errou`) : `${player.name} escolheu esta opção`}
                          >
                            {player.color.emoji}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          
          {gameMode === 'multiplayer' && !showResults && currentPlayer && (
            <div className="multiplayer-instructions">
              {currentPlayerHasAnswered ? (
                <p>
                  ✓ <strong>{currentPlayer.name}</strong> já respondeu. Aguardando próximo jogador...
                </p>
              ) : (
                <p>
                  👥 <strong>{currentPlayer.name}</strong>, é sua vez! Escolha a alternativa correta.
                </p>
              )}
              <p className="progress-text">
                Pergunta {currentQuestionIndex + 1} de {questions.length} | 
                Próximo jogador: {players[(currentQuestionIndex + 1) % players.length]?.name}
              </p>
            </div>
          )}
        </div>
      )}

      {showResults && (
        <div className="results-section">
          <div className="current-scores">
            <h4>Pontuação Atual:</h4>
            {ranking.map(player => (
              <div key={player.playerId} className="score-item" style={{ borderColor: player.color.color }}>
                <span>{player.color.emoji} {player.name}: {player.score} pontos</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {gameMode === 'multiplayer' && Object.keys(answers[currentQuestionIndex] || {}).length === players.length && !showResults && (
        <div className="reveal-answers-section">
          <button onClick={() => setShowResults(true)} className="reveal-button">
            Revelar Respostas Corretas
          </button>
        </div>
      )}

      <div className="quiz-actions">
        {gameMode === 'solo' && showResults ? (
          <button onClick={nextQuestion} className="next-button">
            {currentQuestionIndex < questions.length - 1 ? 'Próxima Pergunta' : 'Ver Ranking Final'}
          </button>
        ) : gameMode === 'solo' && !showResults ? (
          <p className="waiting-text">Selecione uma resposta acima</p>
        ) : gameMode === 'multiplayer' && currentPlayerHasAnswered && !showResults ? (
          <p className="waiting-text">
            {currentPlayer?.name} respondeu! Mostrando resultado em breve...
          </p>
        ) : gameMode === 'multiplayer' && showResults ? (
          <p className="waiting-text">
            Resultado mostrado! Próxima pergunta automaticamente...
          </p>
        ) : gameMode === 'multiplayer' && !currentPlayerHasAnswered ? (
          <p className="waiting-text">
            {currentPlayer?.name}, escolha sua resposta acima
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default QuizGame;
